import { useHomeQuery } from '@/queries/home.query';
import { motion } from 'framer-motion';
import { ExternalLink, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ContainerFluid } from './container/Container';
import { BodySm, H5 } from './ui/typography/Typography';

export default function OfficeLocationsAdvanced() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const searchRef = useRef(null);

  const { data, isLoading, isError } = useHomeQuery();
  const offices = useMemo(() => data?.data?.office_locations || [], [data]);

  /* ================= DEBOUNCE SEARCH ================= */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  /* ================= FILTER ================= */
  const filtered = useMemo(() => {
    if (!debouncedQuery) return offices;
    const q = debouncedQuery.toLowerCase();
    return offices.filter((o) => `${o.title} ${o.address}`.toLowerCase().includes(q));
  }, [debouncedQuery, offices]);

  /* ================= ACTIVE INDEX FIX ================= */
  useEffect(() => {
    if (filtered.length === 0) {
      setActiveIndex(null);
    } else if (activeIndex === null || activeIndex >= filtered.length) {
      setActiveIndex(0);
    }
  }, [filtered, activeIndex]);

  /* ================= KEYBOARD SHORTCUT ================= */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setQuery('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* ================= LOADING STATE ================= */
  if (isLoading) return null;

  /* ================= ERROR STATE ================= */
  if (isError) return null;

  if (offices.length === 0) {
    return (
      <section className="py-16">
        <ContainerFluid>
          <div className="bg-white rounded-2xl border p-8 text-center text-gray-500">
            Tidak ada data kantor.
          </div>
        </ContainerFluid>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-b from-white to-primary-100 relative">
      <ContainerFluid>
        {/* ================= HEADER ================= */}
        <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-4 mb-6">
          <div className="text-center lg:text-left">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-800"
            >
              Lokasi Kantor
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="text-sm text-gray-500"
            >
              Cari berdasarkan nama atau alamat.
            </motion.p>
          </div>

          {/* ================= SEARCH ================= */}
          <div className="relative flex items-center bg-white rounded-full px-3 py-4 shadow-sm border w-full">
            <Search className="w-5 h-5 mx-4 text-primary-400" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cari nama kantor, alamat..."
              className="ml-2 w-full outline-none bg-transparent"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="ml-2 px-3 rounded-full bg-primary-100 text-primary-700"
              >
                x
              </button>
            )}
          </div>
        </div>

        {/* ================= EMPTY RESULT ================= */}
        {filtered.length === 0 && (
          <div className="text-center py-16 bg-white rounded-2xl border shadow">
            <p className="text-gray-500">Tidak ada kantor ditemukan dari pencarian Anda.</p>
          </div>
        )}

        {/* ================= RESULT ================= */}
        {filtered.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* ===== LEFT LIST ===== */}
            <div>
              <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
                <div className="p-4 border-b text-sm">
                  Hasil: <strong>{filtered.length}</strong>
                </div>

                <div className="max-h-[400px] overflow-auto">
                  <ul className="divide-y">
                    {filtered.map((item, idx) => (
                      <li key={idx}>
                        <button
                          onClick={() => setActiveIndex(idx)}
                          className={`w-full text-left px-4 py-3 hover:bg-primary-50 ${
                            activeIndex === idx ? 'bg-primary-50' : ''
                          }`}
                        >
                          <H5 className="mb-1">
                            {item.title} ({item.region})
                          </H5>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* ===== RIGHT DETAIL ===== */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl border shadow-lg p-6">
                <H5>{filtered[activeIndex]?.title}</H5>

                <div className="mt-4 bg-primary-50 border rounded-xl p-4">
                  <H5 className="mb-2">Informasi Lengkap</H5>
                  <BodySm className="text-gray-700">{filtered[activeIndex]?.address}</BodySm>

                  <div className="mt-4">
                    <a
                      href={filtered[activeIndex]?.maps}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Lihat di Google Maps
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </ContainerFluid>
    </section>
  );
}
